import React, { useCallback, useEffect, useRef, useState } from "react";
import { TableDropDown } from "@solsys/solsys-reactjs";
import { gql, useQuery } from "@apollo/client";

const gqlQuery = gql`
  query craneDropDown($crane_list_request: crane_list_request) {
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
  }
`;

const mapCraneEdges = (edges) =>
  (edges ?? []).map((item) => ({
    ...item,
    ID: item.id,
    Name: item.name,
  }));

const CraneDropDown = ({ selected, ...props }) => {
  selected = selected === -1 ? undefined : selected;

  const truncateLength = 60;

  const [requestState, setRequestState] = useState({
    page: 1,
    pageSize: 20,
    search: "",
  });
  const { page, pageSize, search } = requestState;

  const [accumulatedList, setAccumulatedList] = useState([]);

  const filtersKey = JSON.stringify(props.filters ?? []);
  const sortingKey = JSON.stringify(props.sorting ?? []);

  let variables = {
    crane_list_request: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  let renderVariables = {
    crane_list_request: {
      pagination: {
        limit: truncateLength,
        offset: 0,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  // Resolve selected IDs whether they arrive as numbers or strings.
  // TableDropDown renders selectRender[0] for single-select, so this filter
  // must include exactly the selected crane(s) or the label is wrong.
  const selectedIds = Array.isArray(selected)
    ? selected.filter((v) => v != null && v !== "")
    : (selected != null && selected !== "" ? [selected] : []);
  if (selectedIds.length > 0) {
    renderVariables.crane_list_request.filters.push({
      field: "id",
      operator: "in",
      value: `(${selectedIds.join(", ")})`,
    });
  }

  if (search && search !== "")
    variables.crane_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${search}%`,
    });
  if (props.filters?.length > 0) {
    props.filters.forEach((f) =>
      variables.crane_list_request.filters.push(f)
    );
  }
  if (props.sorting?.length > 0) {
    props.sorting.forEach(
      (s) => (variables.crane_list_request.sorting = [s])
    );
  }

  let { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
    variables: renderVariables,
  });
  const renderLoading = renderObject.loading;
  const renderError = renderObject.error;
  const renderData = renderObject.data;

  useEffect(() => {
    renderRefetch();
  }, [selected, renderRefetch]);

  const renderEntityListResponse = renderData?.crane;
  const renderEntityRaw = renderEntityListResponse?.edges ?? [];
  const renderEntity = renderEntityRaw.map(item => ({
    ...item,
    ID: item.id,
    Name: item.name
  }));

  const { refetch: refetchCrane, loading, error, data } = useQuery(gqlQuery, {
    variables,
  });

  useEffect(() => {
    if (!props.disabled) refetchCrane();
  }, [page, search, filtersKey, sortingKey, refetchCrane, props.disabled]);

  // Reset accumulation when search or filters change (new result set).
  useEffect(() => {
    setAccumulatedList([]);
    setRequestState((s) => ({ ...s, page: 1 }));
  }, [search, filtersKey, sortingKey]);

  // Merge each fetched page into the list shown in the dropdown.
  useEffect(() => {
    if (loading) return;
    const edges = data?.crane?.edges;
    if (!edges) return;
    const mapped = mapCraneEdges(edges);
    setAccumulatedList((prev) => {
      if (page === 1) return mapped;
      const seen = new Set(prev.map((x) => Number(x.id)));
      const toAdd = mapped.filter((x) => !seen.has(Number(x.id)));
      return [...prev, ...toAdd];
    });
  }, [data, page, loading]);

  const totalCountRef = useRef(0);
  totalCountRef.current = data?.crane?.totalCount ?? 0;
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedList.length;

  const onSearch = (event) => {
    let { value } = event.target;
    setRequestState((s) => ({
      ...s,
      search: value,
      page: 1,
    }));
  };

  const paginationChange = (newPage) => {
    setRequestState((s) => ({
      ...s,
      page: newPage,
    }));
  };

  const addMoreResultsHandler = useCallback(() => {
    if (props.disabled) return;
    setRequestState((s) => {
      const total = totalCountRef.current;
      if (accumulatedLenRef.current >= total) return s;
      return { ...s, page: s.page + 1 };
    });
  }, [props.disabled]);

  const entityCount = data?.crane?.totalCount ?? 0;

  if (error) console.error(`Crane drop down data error : ${error.message}`);
  if (renderError)
    console.error(`Crane drop down render error : ${renderError.message}`);

  const headers = [
    { label: "ID" },
    { label: "Name" }
  ];

  return (
    <TableDropDown
      { ...props }
      items={ accumulatedList }
      loading={ loading }
      renderLoading={ renderLoading }
      multiple={ props.multiple }
      searchEnabled={ props.searchEnabled }
      searchValue={ search }
      onSearch={ onSearch }
      selectValue={ selected }
      selectRender={ renderEntity }
      onSelect={ props.onChange || props.onSelect }
      dataKey={ "id" }
      identityColumnKey={ "ID" }
      renderColumnKey={ "Name" }
      headers={ headers }
      truncateLength={ truncateLength }
      entityCount={ entityCount }
      onPaginationChange={ paginationChange }
      addMoreResults={
        typeof props.addMoreResults === "function"
          ? props.addMoreResults
          : addMoreResultsHandler
      }
    />
  );
};

export default CraneDropDown;
